import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { TOOL_RENDERERS } from '../Chat/tool-renderers';

interface ToolCallWrapperProps {
  toolCall: any;
}

function ToolCallWrapper({ toolCall }: ToolCallWrapperProps) {
  const [expanded, setExpanded] = useState(false);
  const { t } = useTranslation('chat');

  // Parse the tool call if it's a string, with error handling
  let parsedToolCall = toolCall;
  try {
    if (typeof toolCall === 'string') {
      parsedToolCall = JSON.parse(toolCall);
    }
  } catch (error) {
    console.error('Error parsing tool call:', error);
    // Keep the original value if parsing fails
  }

  // Try to extract the tool name from different possible structures
  const toolName =
    parsedToolCall?.name || parsedToolCall?.toolName || parsedToolCall?.function?.name || t('display.unknown-tool');

  // Look up the specific tool renderer
  const tool = TOOL_RENDERERS[toolName];

  // Render both specific tool renderer AND default renderer for admin purposes
  const renderSpecificTool = () => {
    if (!tool || !tool.result) return null;

    try {
      // For fetch_user_images, the result renderer expects { result } where result.images is the array
      // Check the actual structure of parsedToolCall to see what contains the images
      let toolResult = parsedToolCall;

      // Try different possible result locations
      if (parsedToolCall.result) {
        toolResult = parsedToolCall.result;
      } else if (parsedToolCall.output) {
        toolResult = parsedToolCall.output;
      } else if (parsedToolCall.response) {
        toolResult = parsedToolCall.response;
      }

      // Create a formatted tool call object that matches what the Chat component expects
      const formattedToolCall = {
        toolName: toolName,
        result: toolResult,
        state: 'result',
      };

      // Check if the tool renderer expects the new context pattern or old pattern
      const renderer = tool.result;
      const rendererString = renderer.toString();

      // If it uses destructured parameters, pass the context object
      if (rendererString.includes('{ result }')) {
        return renderer({ result: toolResult, expanded, setExpanded });
      } else if (rendererString.includes('{ toolCall') || rendererString.includes('{toolCall')) {
        return renderer({ toolCall: formattedToolCall, expanded, setExpanded });
      }

      // Otherwise, use the old pattern (just pass toolCall directly)
      return renderer(formattedToolCall);
    } catch (error) {
      console.error('Error rendering tool call:', error);
      console.error('Tool call data was:', parsedToolCall);
      return null;
    }
  };

  const specificToolRender = renderSpecificTool();

  // Always show default collapsible JSON for admin purposes
  const defaultRender = (
    <div className="my-2">
      <div
        className="flex items-center justify-between cursor-pointer select-none"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="text-xs text-gray-500 font-medium">
          {t('display.tool-call')} <span className="text-gray-700 font-semibold">{toolName}</span>
        </span>
        <ChevronRight
          className={`ml-2 w-3 h-3 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-90' : 'rotate-0'}`}
        />
      </div>
      {expanded && (
        <div className="mt-2">
          <pre className="text-xs text-gray-700 font-mono whitespace-pre-wrap bg-white p-4">
            {JSON.stringify(parsedToolCall, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );

  return (
    <div>
      {/* Show specific tool renderer first if available */}
      {specificToolRender && <div className="my-2">{specificToolRender}</div>}
      {/* Always show default collapsible for admin access to raw data */}
      {defaultRender}
    </div>
  );
}

export default ToolCallWrapper;
